import { apiClient } from '../api';
import type { paths } from '../../types/ledger/generated';

// Extract types from the generated OpenAPI types
type JournalsListResponse = paths['/journals']['get']['responses']['200']['content']['application/json'];
type JournalResponse = paths['/journals/{id}']['get']['responses']['200']['content']['application/json'];
type PostJournalBatchRequest = paths['/journals/batch']['post']['requestBody']['content']['application/json'];
type PostJournalBatchResponse = paths['/journals/batch']['post']['responses']['201']['content']['application/json'];
type ReverseJournalRequest = paths['/journals/{id}/reverse']['post']['requestBody']['content']['application/json'];
type ReverseJournalResponse = paths['/journals/{id}/reverse']['post']['responses']['201']['content']['application/json'];
type TrialBalanceResponse = paths['/reports/trial-balance']['get']['responses']['200']['content']['application/json'];
type AccountLedgerResponse = paths['/reports/account-ledger/{accountCode}']['get']['responses']['200']['content']['application/json'];
type ChartOfAccountsResponse = paths['/reports/chart-of-accounts']['get']['responses']['200']['content']['application/json'];
type PeriodsResponse = paths['/reports/periods']['get']['responses']['200']['content']['application/json'];

// Query parameter types
type JournalsListParams = paths['/journals']['get']['parameters']['query'];
type AccountLedgerParams = paths['/reports/account-ledger/{accountCode}']['get']['parameters']['query'];
type TrialBalanceParams = paths['/reports/trial-balance']['get']['parameters']['query'];

class LedgerService {
  private generateIdempotencyKey(operation: string, ...params: string[]): string {
    // Deterministic key for true idempotency (no timestamp)
    return `${operation}:${params.join(':')}`;
  }

  // Journal Management Methods
  async getJournals(params: JournalsListParams = {}): Promise<JournalsListResponse> {
    return apiClient.getLedgerJournals(params) as Promise<JournalsListResponse>;
  }

  async getJournalById(id: string): Promise<JournalResponse> {
    return apiClient.getLedgerJournalById(id) as Promise<JournalResponse>;
  }

  async postJournalBatch(request: PostJournalBatchRequest): Promise<PostJournalBatchResponse> {
    const idempotencyKey = this.generateIdempotencyKey('batch', Date.now().toString());
    return apiClient.postLedgerJournalBatch(request, idempotencyKey) as Promise<PostJournalBatchResponse>;
  }

  async reverseJournal(journalId: string, reason: string): Promise<ReverseJournalResponse> {
    const idempotencyKey = this.generateIdempotencyKey('reverse', journalId);
    return apiClient.reverseLedgerJournal(journalId, reason, idempotencyKey) as Promise<ReverseJournalResponse>;
  }

  // Reporting Methods
  async getTrialBalance(params: TrialBalanceParams): Promise<TrialBalanceResponse> {
    return apiClient.getLedgerTrialBalance(params) as Promise<TrialBalanceResponse>;
  }

  async getAccountLedger(
    accountCode: string, 
    params: AccountLedgerParams = {}
  ): Promise<AccountLedgerResponse> {
    return apiClient.getLedgerAccountLedger(accountCode, params) as Promise<AccountLedgerResponse>;
  }

  async getChartOfAccounts(): Promise<ChartOfAccountsResponse> {
    return apiClient.getLedgerChartOfAccounts() as Promise<ChartOfAccountsResponse>;
  }

  async getPeriods(): Promise<PeriodsResponse> {
    return apiClient.getLedgerPeriods() as Promise<PeriodsResponse>;
  }
}

export const ledgerService = new LedgerService();
export type {
  JournalsListResponse,
  JournalResponse,
  PostJournalBatchRequest,
  PostJournalBatchResponse,
  ReverseJournalRequest,
  ReverseJournalResponse,
  TrialBalanceResponse,
  AccountLedgerResponse,
  ChartOfAccountsResponse,
  PeriodsResponse,
  JournalsListParams,
  AccountLedgerParams,
  TrialBalanceParams,
};